/**
 * Is this even shaped like a UPI ID?
 *
 * The only check that costs nothing and cannot be switched off by an account
 * setting. It matters because the provider-side validator is inert on our
 * account (`validateRazorpayVpa` fails open when `validate/vpa` is not
 * activated), and the UPI field had no format check at all — `ziyak`,
 * `9876543210` and `ziyak@` all reached a Razorpay order, whose collect submit
 * was then refused before any payment object existed. From the dashboard that
 * looks like an order nobody ever paid; from the payer's side it looked like a
 * stuck screen.
 *
 * Deliberately permissive. A format check that guesses wrong takes UPI down for
 * whoever it guessed wrong about, so this rejects only what cannot be a VPA and
 * leaves every judgement call to the charge.
 */

/**
 * `identifier@handle`, per the NPCI addressing rules.
 *
 * The handle is alphanumeric rather than the stricter alphabetic form the spec
 * suggests: a bank could ship a handle with a digit in it tomorrow, and this
 * check refusing a real address is a worse outcome than it waving through a
 * fake one.
 */
const VPA_PATTERN = /^[a-zA-Z0-9](?:[a-zA-Z0-9.\-_]{0,255})@[a-zA-Z0-9]{2,64}$/;

/** Trim and case-fold. UPI handles are case-insensitive and stored lowercase. */
export const normalizeVpa = (raw: string) => raw.trim().toLowerCase();

export type VpaFormatCheck =
  | { ok: true; vpa: string }
  | { ok: false; message: string };

export function checkVpaFormat(raw: string): VpaFormatCheck {
  const vpa = normalizeVpa(raw);

  if (!vpa) {
    return { ok: false, message: "Enter a UPI ID." };
  }

  // Named separately from the pattern because it is the single most common
  // mistake — people type their phone number or their bank username — and
  // "add @yourbank" is actionable where "invalid UPI ID" is not.
  if (!vpa.includes("@")) {
    return {
      ok: false,
      message: "A UPI ID looks like name@bank. Add the @ and your bank handle.",
    };
  }

  if (!VPA_PATTERN.test(vpa)) {
    return {
      ok: false,
      message:
        "That doesn't look like a UPI ID. It usually looks like name@bank.",
    };
  }

  return { ok: true, vpa };
}

/**
 * Reason codes where the address is the problem, not the money.
 *
 * From Razorpay's UPI error reference:
 *
 * - `invalid_vpa` — "the customer not being a valid user on the UPI App",
 *   which covers a typo and an unregistered handle alike.
 * - `vpa_resolution_failed` — the id could not be resolved to an account.
 *
 * Everything else — insufficient funds, bank downtime, a cancelled approval —
 * is deliberately absent. Sending somebody back to re-type a UPI ID that was
 * never the problem is its own dead end, so this stays narrow and the general
 * failure path keeps everything it does not recognise.
 */
const VPA_REJECTION_REASONS = new Set(["invalid_vpa", "vpa_resolution_failed"]);

export const UPI_ID_NOT_FOUND_MESSAGE =
  "No UPI account found for that ID. Check it for a typo, or use a different one.";

export const UPI_ID_REJECTED_MESSAGE =
  "That UPI ID didn't work — check it for a typo, or use a different one. No money was taken.";

export function isVpaRejection(
  providerError: { reason?: string | null } | null | undefined,
): boolean {
  const reason = providerError?.reason?.trim().toLowerCase();
  return Boolean(reason && VPA_REJECTION_REASONS.has(reason));
}

/**
 * The account-holder name, if there is one worth showing.
 *
 * NPCI hands the name back masked — a real live response for a valid address
 * was `"ZI*********************"`. Two characters and twenty-one asterisks is
 * not a name, and rendering it after "Verified ·" reads as a broken string
 * rather than a confirmation, so a masked value is treated as no name at all
 * and the caller falls back to confirming without one.
 */
export function describeVpaHolderName(
  raw: string | null | undefined,
): string | null {
  const name = raw?.trim();
  if (!name || name.includes("*")) return null;
  return name;
}
